/**
 * DealStrategyView — read-only renderer for an AI-generated, milestone-
 * based deal strategy. Shared by the admin Strategy tab
 * (DealRoomDetailPage) and the public investor/startup view
 * (DealRoomViewPage). Tolerant of partial payloads — every section is
 * optional.
 */
import React from 'react';
import {
  Layers,
  ShieldAlert,
  TrendingUp,
  CheckCircle2,
  Target,
  ArrowRight,
} from 'lucide-react';

interface Milestone {
  milestone?: string;
  metric?: string;
  target?: string;
}
interface Phase {
  name?: string;
  amount_usd?: number;
  timing?: string;
  valuation_or_cap_usd?: number;
  release_milestones?: Milestone[];
  startup_value_add?: string[];
  investor_protections?: string[];
}
interface RiskMitigation {
  risk?: string;
  severity?: string;
  mitigation?: string;
}
interface ValueAdd {
  initiative?: string;
  expected_impact?: string;
  owner?: string;
  timeline_months?: number;
}
export interface DealStrategyJson {
  overview?: string;
  recommended_instrument?: string;
  total_capital_usd?: number;
  phases?: Phase[];
  risk_mitigations?: RiskMitigation[];
  startup_benefits?: string[];
  investor_benefits?: string[];
  value_add_initiatives?: ValueAdd[];
  success_metrics?: string[];
  next_steps?: string[];
}

const usd = (n?: number) => {
  if (n == null) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
};

const sevChip = (s?: string) => {
  const m: Record<string, string> = {
    high: 'bg-red-50 text-red-700 border-red-200',
    medium: 'bg-amber-50 text-amber-700 border-amber-200',
    low: 'bg-gray-50 text-gray-600 border-gray-200',
  };
  return m[(s || '').toLowerCase()] || m.low;
};

const Card: React.FC<{
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}> = ({ title, icon: Icon, children }) => (
  <section className="bg-white border border-gray-200 rounded-xl p-5">
    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
      <Icon className="w-4 h-4 text-blue-600" />
      {title}
    </h3>
    {children}
  </section>
);

const DealStrategyView: React.FC<{ strategy: DealStrategyJson }> = ({ strategy }) => {
  if (!strategy) return null;
  return (
    <div className="space-y-4">
      {(strategy.overview || strategy.recommended_instrument || strategy.total_capital_usd != null) && (
        <section className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-xl p-5">
          {strategy.overview && (
            <p className="text-sm text-gray-800 leading-relaxed">{strategy.overview}</p>
          )}
          <div className="flex flex-wrap gap-4 mt-3 text-sm">
            {strategy.recommended_instrument && (
              <div>
                <span className="text-xs uppercase tracking-wide text-gray-500">Instrument</span>
                <p className="font-medium text-gray-900">{strategy.recommended_instrument}</p>
              </div>
            )}
            {strategy.total_capital_usd != null && (
              <div>
                <span className="text-xs uppercase tracking-wide text-gray-500">Total capital</span>
                <p className="font-medium text-gray-900">{usd(strategy.total_capital_usd)}</p>
              </div>
            )}
          </div>
        </section>
      )}

      {strategy.phases && strategy.phases.length > 0 && (
        <Card title="Phased investment" icon={Layers}>
          <div className="space-y-3">
            {strategy.phases.map((p, i) => (
              <div key={i} className="border border-gray-100 rounded-lg p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h4 className="font-semibold text-gray-900">{p.name || `Phase ${i + 1}`}</h4>
                  <div className="text-sm text-gray-700">
                    {usd(p.amount_usd)}
                    {p.timing ? <span className="text-gray-400"> · {p.timing}</span> : null}
                    {p.valuation_or_cap_usd ? (
                      <span className="text-gray-400"> · cap {usd(p.valuation_or_cap_usd)}</span>
                    ) : null}
                  </div>
                </div>
                {p.release_milestones && p.release_milestones.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                      Release milestones
                    </p>
                    <ul className="space-y-1">
                      {p.release_milestones.map((m, j) => (
                        <li key={j} className="text-sm text-gray-700 flex items-start gap-1.5">
                          <Target className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
                          <span>
                            {m.milestone}
                            {m.target ? <span className="text-gray-500"> — target: {m.target}</span> : null}
                            {m.metric ? <span className="text-gray-400"> ({m.metric})</span> : null}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  {p.startup_value_add && p.startup_value_add.length > 0 && (
                    <div>
                      <p className="text-xs uppercase tracking-wide text-emerald-700 mb-1">Startup value-add</p>
                      <ul className="text-sm text-gray-700 list-disc list-inside space-y-0.5">
                        {p.startup_value_add.map((s, k) => <li key={k}>{s}</li>)}
                      </ul>
                    </div>
                  )}
                  {p.investor_protections && p.investor_protections.length > 0 && (
                    <div>
                      <p className="text-xs uppercase tracking-wide text-purple-700 mb-1">Investor protections</p>
                      <ul className="text-sm text-gray-700 list-disc list-inside space-y-0.5">
                        {p.investor_protections.map((s, k) => <li key={k}>{s}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {strategy.value_add_initiatives && strategy.value_add_initiatives.length > 0 && (
        <Card title="Value-add initiatives" icon={TrendingUp}>
          <ul className="space-y-2">
            {strategy.value_add_initiatives.map((v, i) => (
              <li key={i} className="text-sm text-gray-700">
                <span className="font-medium text-gray-900">{v.initiative}</span>
                {v.expected_impact ? <span className="text-emerald-700"> — {v.expected_impact}</span> : null}
                <span className="text-gray-400">
                  {v.owner ? ` · ${v.owner}` : ''}
                  {v.timeline_months ? ` · ${v.timeline_months}mo` : ''}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {strategy.risk_mitigations && strategy.risk_mitigations.length > 0 && (
        <Card title="Risk mitigations" icon={ShieldAlert}>
          <ul className="space-y-2">
            {strategy.risk_mitigations.map((r, i) => (
              <li key={i} className="text-sm">
                <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border mr-2 ${sevChip(r.severity)}`}>
                  {(r.severity || 'low').toUpperCase()}
                </span>
                <span className="font-medium text-gray-900">{r.risk}</span>
                {r.mitigation ? (
                  <span className="text-gray-600 flex items-start gap-1 mt-0.5 ml-1">
                    <ArrowRight className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-400" />
                    {r.mitigation}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {strategy.startup_benefits && strategy.startup_benefits.length > 0 && (
          <Card title="Why it works for the startup" icon={CheckCircle2}>
            <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
              {strategy.startup_benefits.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </Card>
        )}
        {strategy.investor_benefits && strategy.investor_benefits.length > 0 && (
          <Card title="Why it works for the investor" icon={CheckCircle2}>
            <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
              {strategy.investor_benefits.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </Card>
        )}
      </div>

      {strategy.success_metrics && strategy.success_metrics.length > 0 && (
        <Card title="Success metrics" icon={Target}>
          <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
            {strategy.success_metrics.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </Card>
      )}

      {strategy.next_steps && strategy.next_steps.length > 0 && (
        <Card title="Next steps" icon={ArrowRight}>
          <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
            {strategy.next_steps.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </Card>
      )}
    </div>
  );
};

export default DealStrategyView;
